import { Component, For, Match, Show, Switch, createMemo, createSignal, onMount } from 'solid-js'
import { settingStore, userStore, i18nStore } from '/web/store'
import { AppSchema } from '/common/types'
import { Pill, SolidCard, TitleCard } from '/web/shared/Card'
import Button from '/web/shared/Button'
import { TierCard } from './TierCard'
import { ConfirmModal } from '/web/shared/Modal'
import { PatreonControls } from '../Settings/PatreonOauth'
import { getUserSubscriptionTier } from '/common/util'
import { isLoggedIn } from '/web/store/api'
import { useNavigate } from '@solidjs/router'
import { ModelList } from '/web/shared/PresetSettings/Agnaistic'

export const SubscriptionPage: Component<{}> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const settings = settingStore((s) => s.config)
  const user = userStore((s) => ({ user: s.user, sub: s.sub, billingLoading: s.billingLoading }))
  const cfg = userStore((s) => {
    const tier = s.user ? getUserSubscriptionTier(s.user, s.tiers) : null

    return {
      type: tier?.type ?? 'none',
      tier: tier?.tier,
      level: tier?.level ?? -1,
      tiers: s.tiers.sort((l, r) => r.level - l.level),
      downgrade: s.subStatus?.downgrading?.tierId,
    }
  })

  const nav = useNavigate()
  const [showUnsub, setUnsub] = createSignal(false)
  const [showUpgrade, setUpgrade] = createSignal<AppSchema.SubscriptionTier>()
  const [showDowngrade, setDowngrade] = createSignal<AppSchema.SubscriptionTier>()
  const [showModels, setShowModels] = createSignal(false)

  const hasExpired = createMemo(() => {
    // We should leave this out. It's possible a user can subscribe multiple ways
    // if (cfg.type === 'patreon' || cfg.type === 'manual') return true

    // if (!user.user?.billing?.cancelling) return false
    if (!user.user?.billing) return true
    if (user.user.billing.status === 'cancelled') return true

    const threshold = new Date(user.user.billing.validUntil)
    return threshold.valueOf() < Date.now()
  })

  const candidates = createMemo(() => {
    return cfg.tiers
      .filter((t) => {
        const isPatronOf = user.sub?.type === 'patreon' && cfg.tier?._id === t._id
        isPatronOf
        // if (isPatronOf) return false

        const usable = t.level !== user.sub?.level
        return usable && t.enabled && !t.deletedAt && !!t.productId
      })
      .sort((l, r) => l.level - r.level)
  })

  const renews = createMemo(() => {
    if (cfg.type === 'manual') {
      const last = new Date(user.user?.manualSub?.expiresAt!)
      return last.toLocaleDateString()
    }
    if (!user.user?.billing) return ''
    if (cfg.type === 'patreon') return ''
    const last = new Date(user.user.billing.validUntil)
    return last.toLocaleDateString()
  })

  const onSubscribe = (tierId: string) => {
    userStore.startCheckout(tierId)
  }

  const currentText = createMemo(() => {
    if (cfg.type === 'manaul') return i18n.t.profile_sub_valid_until
    if (cfg.type === 'patreon') return i18n.t.profile_sub_patreon_subscriber

    if (user.user?.billing?.status === 'active') {
      if (user.user?.billing?.cancelling) return i18n.t.profile_sub_cancels_at
      return i18n.t.profile_sub_renews_at
    }
    return i18n.t.profile_sub_valid_until
  })

  const canResume = createMemo(() => {
    if (!user.user?.billing?.cancelling) return false
    if (user.user.billing.status === 'cancelled') return false

    const threshold = new Date(user.user.billing.validUntil)
    return threshold.valueOf() > Date.now()
  })

  onMount(() => {
    userStore.subscriptionStatus()
  })

  return (
    <>
      <div class="flex flex-col gap-2">
        <div class="flex w-full flex-col items-center gap-4">
          <SolidCard class="flex flex-col gap-2" border>
            <p class="flex flex-wrap justify-center text-[var(--hl-500)]">
              <strong>{i18n.t.profile_sub_why_title}</strong>
            </p>
            <p>{i18n.t.profile_sub_why_msg_1}</p>
            <p>{i18n.t.profile_sub_why_msg_2}</p>
            <p>{i18n.t.profile_sub_why_msg_3}</p>
          </SolidCard>

          <Show when={settings.serverConfig?.supportEmail}>
            <SolidCard>
              {i18n.t.profile_sub_support_contact}{' '}
              <a class="link" href={`mailto:${settings.serverConfig?.supportEmail}`}>
                {settings.serverConfig?.supportEmail?.split(',')[0]}
              </a>
            </SolidCard>
          </Show>

          <Button schema="primary" onClick={() => setShowModels(true)}>
            {i18n.t.profile_sub_view_models}
          </Button>

          <PatreonControls />

          <Show when={user.sub?.level! > 0}>
            <h3 class="font-bold">{i18n.t.profile_sub_current_title}</h3>
            <Show when={user.user?.billing}>
              <a class="link" href={settings.serverConfig?.stripeCustomerPortal} target="_blank">
                {i18n.t.profile_sub_update_payment}
              </a>
            </Show>
            <TierCard tier={cfg.tier!}>
              <div class="flex flex-col items-center gap-2">
                <div class="text-700 text-sm italic">
                  {currentText()} {renews()}
                </div>
                <Pill type="green">
                  {i18n.t.profile_sub_via}{' '}
                  {user.sub?.type === 'manual'
                    ? i18n.t.profile_sub_via_gift
                    : user.sub?.type === 'patreon'
                    ? i18n.t.profile_sub_via_patreon
                    : user.sub?.type === 'native'
                    ? i18n.t.profile_sub_via_stripe
                    : i18n.t.profile_sub_via_none}
                </Pill>
                <Switch>
                  <Match when={cfg.downgrade && cfg.tier!._id !== cfg.downgrade}>
                    {i18n.t.profile_sub_set_to_downgrade}
                    <Button
                      schema="green"
                      onClick={() => userStore.modifySubscription(cfg.tier?._id!)}
                      disabled={user.billingLoading}
                    >
                      {i18n.t.profile_sub_cancel_downgrade}
                    </Button>
                  </Match>
                  <Match when={canResume()}>
                    {i18n.t.profile_sub_scheduled_to_cancel}
                    <Button
                      schema="green"
                      onClick={userStore.resumeSubscription}
                      disabled={user.billingLoading}
                    >
                      {i18n.t.profile_sub_resume}
                    </Button>
                  </Match>
                  <Match when={cfg.type === 'manual'}>
                    <SolidCard
                      bg="bg-700"
                      class="flex w-1/2 justify-center text-lg font-bold text-[var(--green-600)]"
                    >
                      {i18n.t.profile_sub_enjoy}
                    </SolidCard>
                  </Match>
                  <Match when>
                    <SolidCard
                      bg="bg-700"
                      class="flex w-1/2 justify-center text-lg font-bold text-[var(--green-600)]"
                    >
                      {i18n.t.profile_sub_subscribed}
                    </SolidCard>
                  </Match>
                </Switch>
              </div>
            </TierCard>
          </Show>

          <Show when={candidates().length > 0}>
            <div class="font-bold">{i18n.t.profile_sub_options}</div>
          </Show>

          <Show when={settings.patreon}>
            <TitleCard center title={<span class="text-[var(--hl-500)]">Patreon</span>}>
              {i18n.t.profile_sub_patreon_msg.split('Patron')[0]}
              <a class="link font-bold" href="https://patreon.com/Agnaistic" target="_blank">
                Patron
              </a>{' '}
              {i18n.t.profile_sub_patreon_msg.split('Patron')[1]}
            </TitleCard>
          </Show>

          <div class="flex w-full flex-wrap justify-center gap-4">
            <For each={candidates()}>
              {(each) => (
                <>
                  <TierCard tier={each} class="sm:w-1/3">
                    <Show when={user.user?.manualSub?.tierId === each._id}>
                      <Pill type="green">{i18n.t.profile_sub_gifted_to_you}</Pill>
                    </Show>
                    <div class="mt-4 flex justify-center">
                      <Switch>
                        <Match when={!isLoggedIn()}>
                          <Button schema="success" onClick={() => nav('/login')}>
                            {i18n.t.profile_sub_login_to_subscribe}
                          </Button>
                        </Match>
                        <Match when={user.sub?.tier._id === each._id}>
                          <Button schema="success" disabled>
                            {i18n.t.profile_sub_subscribed}
                          </Button>
                        </Match>
                        <Match when={cfg.tier && cfg.level < each.level}>
                          <Button
                            schema="success"
                            disabled={canResume() || user.billingLoading}
                            onClick={() => setUpgrade(each)}
                          >
                            {i18n.t.profile_sub_upgrade}
                          </Button>
                        </Match>

                        <Match
                          when={
                            user.sub?.tier &&
                            user.sub.level > each.level &&
                            each._id === cfg.downgrade
                          }
                        >
                          <Button
                            schema="gray"
                            disabled
                            onClick={() => userStore.modifySubscription(each._id)}
                          >
                            {i18n.t.profile_sub_downgrading}
                          </Button>
                        </Match>

                        <Match when={user.sub?.type === 'native' && user.sub.level > each.level}>
                          <Button
                            schema="gray"
                            disabled={canResume() || user.billingLoading}
                            onClick={() => setDowngrade(each)}
                          >
                            {i18n.t.profile_sub_downgrade}
                          </Button>
                        </Match>

                        <Match when={hasExpired() && each._id === user.sub?.tier._id}>
                          <Button schema="success" onClick={() => onSubscribe(each._id)}>
                            {i18n.t.profile_sub_resubscribe}
                          </Button>
                        </Match>

                        <Match when={user.sub?.level! > each.level}>
                          <Button schema="secondary" disabled onClick={() => onSubscribe(each._id)}>
                            {i18n.t.profile_sub_subscribe}
                          </Button>
                        </Match>

                        <Match when>
                          <Button
                            schema="success"
                            disabled={
                              each._id === cfg.tier?._id || canResume() || user.billingLoading
                            }
                            onClick={() => onSubscribe(each._id)}
                          >
                            {i18n.t.profile_sub_subscribe}
                          </Button>
                        </Match>
                      </Switch>
                    </div>
                  </TierCard>
                </>
              )}
            </For>
          </div>

          <div class="flex justify-center">{i18n.t.profile_sub_prices_usd}</div>

          <div class="mt-4 flex gap-4">
            {/* <Button onClick={userStore.validateSubscription} disabled={user.billingLoading}>
              Validate
            </Button> */}
            <Show when={cfg.tier && !hasExpired()}>
              <Button schema="red" onClick={() => setUnsub(true)} disabled={user.billingLoading}>
                {i18n.t.profile_sub_unsubscribe}
              </Button>
            </Show>
          </div>
        </div>
      </div>
      <ConfirmModal
        show={showUnsub()}
        close={() => setUnsub(false)}
        message={i18n.t.profile_sub_confirm_unsubscribe}
        confirm={userStore.stopSubscription}
      />

      <ConfirmModal
        show={!!showUpgrade()}
        close={() => setUpgrade()}
        message={
          <div class="flex flex-col items-center justify-center gap-2">
            <p>{i18n.t.profile_sub_upgrade_charge_msg}</p>
            <p>{i18n.t.profile_sub_confirm_upgrade}</p>
          </div>
        }
        confirm={() => userStore.modifySubscription(showUpgrade()!._id)}
      />

      <ConfirmModal
        show={!!showDowngrade()}
        close={() => setDowngrade()}
        message={
          <div class="flex flex-col items-center justify-center gap-2">
            <p>{i18n.t.profile_sub_downgrade_affect_msg}</p>
            <p>{i18n.t.profile_sub_confirm_downgrade}</p>
          </div>
        }
        confirm={() => userStore.modifySubscription(showDowngrade()!._id)}
      />

      <ModelList show={showModels()} close={() => setShowModels(false)} />
    </>
  )
}
